<template>
  <div class="list-products-page">
    <PageHeader
      :title="t('myShopData.products.title')"
      summary=""
      :back-link-to="{ name: 'shop.system-objects' }"
      :back-link-text="t('navbar.systemData')"
    />

    <div class="page-content">
      <div class="Products">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :show-reload-button="true"
          data-key="id"
          :enable-grouped-actions="true"
          :exportable="true"
          :show-export-all-button="true"
          :export-config="{ entityType: 'products' }"
          :persistent-filters="persistentFilters"
          @export="handleExport"
        >
          <!-- Composite column: product_info (id, reference, name, dates) -->
          <template #cell-product_info="{ data }">
            <div class="product-info-cell">
              <div class="product-row">
                <span
                  v-if="cleanStr(data.name)"
                  class="product-name"
                >
                  {{ cleanStr(data.name) }}
                </span>
                <span
                  v-else
                  class="product-name product-name--empty"
                >
                  {{ t('myShopData.products.noName') }}
                </span>
                <SpmBadge
                  :label="`#${data.product_id}`"
                  :colors="{ bg: '#e0f2fe', color: '#0369a1' }"
                  size="sm"
                  variant="soft"
                />
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: reference -->
          <template #cell-reference="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="cleanStr(data.reference)"
                class="product-reference"
              >
                {{ cleanStr(data.reference) }}
              </span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: image -->
          <template #cell-image="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <img
                v-if="cleanStr(data.image_link)"
                :src="cleanStr(data.image_link)"
                :alt="cleanStr(data.name) || 'Product'"
                class="product-image"
              >
              <div
                v-else
                class="product-image-placeholder"
              >
                <i class="far fa-image" />
              </div>
            </div>
          </template>

          <!-- Single column: link -->
          <template #cell-link="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <a
                v-if="cleanStr(data.link)"
                :href="cleanStr(data.link)"
                target="_blank"
                class="link-icon"
              >
                <i class="far fa-external-link" />
              </a>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Composite column: pricing (price, price_discount, currency) -->
          <template #cell-pricing="{ data }">
            <div class="pricing-cell">
              <div
                v-if="data.price || data.price_discount"
                class="price-container"
              >
                <div
                  v-if="data.price_discount && parseFloat(data.price_discount) > 0"
                  class="price-with-discount"
                >
                  <div class="price-label-group">
                    <span class="price-label">{{ t('myShopData.products.pricing.original') }}</span>
                    <span class="price-value price-value--original">{{ formatAmount(data.price, data.currency) }}</span>
                  </div>
                  <div class="price-label-group">
                    <span class="price-label">{{ t('myShopData.products.pricing.discount') }}</span>
                    <span class="price-value price-value--discount">{{ formatAmount(data.price_discount, data.currency) }}</span>
                  </div>
                </div>
                <div
                  v-else
                  class="price-simple"
                >
                  <span class="price-value">{{ formatAmount(data.price, data.currency) }}</span>
                </div>
              </div>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: lang -->
          <template #cell-lang="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="cleanStr(data.lang)"
                class="lang-badge"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: active -->
          <template #cell-active="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_active === true || data.is_active === 'true' || data.is_active === '1' || data.is_active === 1"
                v-tooltip.top="t('active')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('inactive')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>
        </spmtableapi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { UserState } from '@/composables/User';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';
import { getUserLocale } from '@/composables/user/Users';
import { formatAmount } from '@/components/table-rest-api/formatters';

export default defineComponent({
  name: 'ProductsV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const idUser = UserState.user?.id;
    const userType = UserState.user?.userType;
    const userLocale = ref<string>('en-US');

    // Load user locale asynchronously
    getUserLocale(idUser, userType).then((locale) => {
      userLocale.value = locale || 'en-US';
    });

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/products',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'product_id',
        header: '',
        fields: [{
          name: 'product_id',
          label: t('myShopData.products.headers.id_product'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'product_info',
        header: t('myShopData.products.headers.name'),
        fields: [{
          name: 'name',
          label: t('myShopData.products.headers.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.products.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.products.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'reference',
        header: t('myShopData.products.headers.reference'),
        fields: [{
          name: 'reference',
          label: t('myShopData.products.headers.reference'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'image',
        header: t('myShopData.products.headers.image_link'),
        fields: [{
          name: 'image_link',
          label: t('myShopData.products.headers.image_link'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'link',
        header: t('myShopData.products.headers.link'),
        fields: [{
          name: 'link',
          label: t('myShopData.products.headers.link'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'pricing',
        header: t('myShopData.products.headers.price'),
        fields: [{
          name: 'price',
          label: t('myShopData.products.headers.price'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'price_discount',
          label: t('myShopData.products.headers.price_discount'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'currency',
          label: t('myShopData.products.headers.currency'),
          sortable: false,
          filterable: false,
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.products.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.products.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.products.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.products.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('active'), value: true },
              { label: t('inactive'), value: false },
            ],
          },
        }],
      },
    ];

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO: Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/products',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.products.export.success'));
        // } else {
        //   await showToastError(t('myShopData.products.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.products.export.error'));
      }
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Wrapper to use formatAmount with user locale
    const formatAmountWithLocale = (amount: string | number | null | undefined, currency: string | null | undefined): string => formatAmount(amount, currency, userLocale.value);

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      handleExport,
      cleanStr,
      formatAmount: formatAmountWithLocale,
    };
  },
});
</script>

<style scoped>
/* Product info composite column */
.product-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-info-cell .product-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.product-info-cell .product-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 3px;
}

.product-info-cell .product-reference {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4b5563;
  background: #f9fafb;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #e5e7eb;
}

.product-info-cell .product-name {
  font-weight: 600;
  color: #1f2937;
}

.product-info-cell .product-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.product-info-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Product image */
.product-image {
  max-width: 90px;
  max-height: 50px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.product-image-placeholder {
  width: 90px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  color: #d1d5db;
  font-size: 1.25rem;
}

/* Pricing composite column */
.pricing-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pricing-cell .price-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pricing-cell .price-with-discount {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pricing-cell .price-label-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pricing-cell .price-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  min-width: 45px;
}

.pricing-cell .price-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

.pricing-cell .price-value--original {
  color: #9ca3af;
  font-size: 0.8125rem;
  font-weight: 500;
}

.pricing-cell .price-value--discount {
  color: #1f2937;
  font-weight: 600;
}

.pricing-cell .price-simple {
  display: flex;
  align-items: center;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

/* Link icon */
.link-icon {
  color: #94c840;
  transition: color 0.2s ease;
}

.link-icon:hover {
  color: #7aa832;
}

/* Language badge */
.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}
</style>

<style scoped lang="scss">
/* Page layout */
.products-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
